import { Injectable } from '@angular/core';
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';

/**
 * Marker types supported by the map
 */
export type MarkerType = 'picture' | 'bid_item' | 'activity' | 'delivery' | 'form' | 'multi_item_location' | 'site';

/**
 * RGBA color as array [R, G, B, A] where each value is 0-255
 */
export type RGBAColor = [number, number, number, number];

/**
 * Marker configuration for PictureMarkerSymbol
 */
export interface MarkerConfig {
  url: string;
  width: string;
  height: string;
}

/**
 * Circle geometry for CIM symbols
 */
export interface CircleGeometry {
  rings: number[][][];
}

/**
 * MapSymbolFactory Service
 *
 * Provides factory methods for creating map symbols with caching.
 * Centralizes symbol creation logic for consistent styling across the map.
 *
 * Part of Phase 2.2 of the map maintainability refactor.
 */
@Injectable({ providedIn: 'root' })
export class MapSymbolFactory {
  /** Symbol cache for performance optimization */
  private symbolCache = new Map<string, __esri.Symbol>();

  // ============================================================================
  // Color Constants (matching Figma design)
  // ============================================================================

  /** Primary blue color - #1036D6 */
  static readonly COLOR_PRIMARY_BLUE: RGBAColor = [16, 54, 214, 255];

  /** White color */
  static readonly COLOR_WHITE: RGBAColor = [255, 255, 255, 255];

  /** Shadow color - #C3C7D5 with transparency */
  static readonly COLOR_SHADOW: RGBAColor = [195, 199, 213, 100];

  /** Bid item marker color - #FF6B35 orange */
  static readonly COLOR_BID_ITEM: RGBAColor = [255, 107, 53, 255];

  /** Activity marker color - #4CAF50 green */
  static readonly COLOR_ACTIVITY: RGBAColor = [76, 175, 80, 255];

  /** Delivery marker color - #9C27B0 purple */
  static readonly COLOR_DELIVERY: RGBAColor = [156, 39, 176, 255];

  // ============================================================================
  // Marker Configurations
  // ============================================================================

  /** Marker icon configurations by type */
  private readonly markerConfigs: Record<MarkerType, MarkerConfig> = {
    bid_item: {
      url: '/assets/icons/map/markers/bid_items@4x.png',
      width: '26px',
      height: '36px'
    },
    activity: {
      url: '/assets/icons/map/markers/activities@4x.png',
      width: '26px',
      height: '36px'
    },
    delivery: {
      url: '/assets/map-markers/delivery-marker@4x.png',
      width: '26px',
      height: '36px'
    },
    // Control forms — default to the quality_test icon when no ui_config override
    // is provided. The configurable icon is resolved at marker-build time via
    // getControlFormMarkerConfig() using the ui_config.map_display.icon key.
    form: {
      url: '/assets/icons/map/markers/quality_test.svg',
      width: '26px',
      height: '36px'
    },
    picture: {
      url: '/assets/icons/map/markers/picture.png',
      width: '35px',
      height: '35px'
    },
    site: {
      url: '/assets/icons/map/markers/site.svg',
      width: '20px',
      height: '20px'
    },
    multi_item_location: {
      url: '', // Uses CIM symbol, not PictureMarkerSymbol
      width: '40px',
      height: '40px'
    }
  };

  // ============================================================================
  // Cache Management
  // ============================================================================

  /**
   * Get a cached symbol by key
   * @param cacheKey - Unique key for the symbol
   * @returns Cached symbol or undefined
   */
  getCached<T extends __esri.Symbol>(cacheKey: string): T | undefined {
    return this.symbolCache.get(cacheKey) as T | undefined;
  }

  /**
   * Store a symbol in the cache
   * @param cacheKey - Unique key for the symbol
   * @param symbol - Symbol to cache
   */
  setCache(cacheKey: string, symbol: __esri.Symbol): void {
    this.symbolCache.set(cacheKey, symbol);
  }

  /**
   * Check if a symbol is cached
   * @param cacheKey - Unique key to check
   */
  hasCache(cacheKey: string): boolean {
    return this.symbolCache.has(cacheKey);
  }

  /**
   * Clear the symbol cache
   * Call when switching projects/contexts to free memory
   */
  clearCache(): void {
    this.symbolCache.clear();
  }

  /**
   * Get cache size for debugging
   */
  getCacheSize(): number {
    return this.symbolCache.size;
  }

  // ============================================================================
  // Configuration Accessors
  // ============================================================================

  /**
   * Get marker configuration for a given type
   * @param type - Marker type
   * @returns Marker configuration
   */
  getMarkerConfig(type: MarkerType): MarkerConfig {
    return this.markerConfigs[type];
  }

  /**
   * Get all marker configurations
   */
  getAllMarkerConfigs(): Record<MarkerType, MarkerConfig> {
    return { ...this.markerConfigs };
  }

  // ============================================================================
  // Control Form Marker Icons
  // ============================================================================

  /**
   * Registry mapping known control-form icon keys (from
   * controlForm.ui_config.map_display.icon) to assets in
   * /assets/icons/map/markers/. Keys are configurable per control form;
   * unknown keys fall back to the default (quality_test).
   */
  // Use the @4x raster assets (like bid_item/activity/delivery markers) so the
  // pin stays crisp on HiDPI screens — ESRI rasterizes raw SVGs at the symbol's
  // 26x36 size, which looks soft on retina.
  private readonly CONTROL_FORM_ICON_REGISTRY: Record<string, string> = {
    quality_test: '/assets/icons/map/markers/quality_test@4x.png',
    labor_equipment: '/assets/icons/map/markers/labor_equipment@4x.png',
    issues: '/assets/icons/map/markers/issues@4x.png',
    bid_items: '/assets/icons/map/markers/bid_items@4x.png',
    activities: '/assets/icons/map/markers/activities@4x.png',
    deliveries: '/assets/icons/map/markers/deliveries@4x.png',
  };

  /** Default control-form marker icon when no ui_config override is set */
  private readonly DEFAULT_CONTROL_FORM_ICON = '/assets/icons/map/markers/quality_test@4x.png';

  /**
   * Resolve the marker icon URL for a control form from its ui_config.
   * controlForm.ui_config?.map_display?.icon is either:
   *  - a full http(s) URL (e.g. a custom org icon hosted on S3) — used as-is, or
   *  - a key into the bundled CONTROL_FORM_ICON_REGISTRY.
   * Falls back to the default quality_test icon.
   * NOTE: when custom S3 icons go live, restrict full URLs to an allowlisted
   * host (the SiteNotes asset bucket) and require an @4x pin-shaped asset.
   * @param controlForm - The control_form object (may be null/undefined)
   */
  resolveControlFormIconUrl(controlForm: any): string {
    const icon = controlForm?.ui_config?.map_display?.icon;
    if (!icon) {
      return this.DEFAULT_CONTROL_FORM_ICON;
    }
    if (/^https?:\/\//i.test(icon)) {
      return icon;
    }
    return this.CONTROL_FORM_ICON_REGISTRY[icon] || this.DEFAULT_CONTROL_FORM_ICON;
  }

  // The grey, currentColor-stroked Types-tab glyph paired with each map-marker
  // icon, keyed by the same map_display.icon identifier. Lets a form group's
  // Types-tab toggle show a matching glyph in the standard light style (not the
  // colored map pin).
  private readonly CONTROL_FORM_TYPE_GLYPH_REGISTRY: Record<string, string> = {
    quality_test: 'type-quality',
    labor_equipment: 'type-labor',
    deliveries: 'type-delivery',
    activities: 'type-activities',
    bid_items: 'type-bid-item',
  };
  private readonly DEFAULT_CONTROL_FORM_TYPE_GLYPH = 'type-quality';

  /**
   * Resolve the registered Types-tab glyph name for a control form, from the same
   * ui_config.map_display.icon used for the map marker. Accepts a registry key or
   * a full URL (e.g. an S3 custom icon) — the file basename (sans @Nx suffix and
   * extension) is matched against the glyph registry. Falls back to the default.
   */
  resolveControlFormTypeGlyph(controlForm: any): string {
    const icon = controlForm?.ui_config?.map_display?.icon;
    if (!icon) {
      return this.DEFAULT_CONTROL_FORM_TYPE_GLYPH;
    }
    let key = icon;
    if (/^https?:\/\//i.test(icon)) {
      key = (icon.split('/').pop() || '')
        .replace(/@\d+x/i, '')
        .replace(/\.[a-z0-9]+$/i, '');
    }
    return this.CONTROL_FORM_TYPE_GLYPH_REGISTRY[key] || this.DEFAULT_CONTROL_FORM_TYPE_GLYPH;
  }

  /**
   * Build the PictureMarkerSymbol config for a control form marker using the
   * resolved (configurable) icon. Mirrors the delivery/bid-item picture-marker
   * shape used by the unified marker layer.
   * @param controlForm - The control_form object (may be null/undefined)
   */
  getControlFormMarkerConfig(controlForm: any): MarkerConfig {
    return {
      url: this.resolveControlFormIconUrl(controlForm),
      width: '26px',
      height: '36px',
    };
  }

  // ============================================================================
  // Simple Marker Symbols
  // ============================================================================

  /**
   * Create a simple marker symbol (circle)
   * @param color - Fill color as RGBA array
   * @param size - Marker size in pixels
   * @param outlineColor - Optional outline color
   * @param outlineWidth - Optional outline width
   */
  createSimpleMarkerSymbol(
    color: RGBAColor,
    size: number = 12,
    outlineColor?: RGBAColor,
    outlineWidth: number = 1
  ): SimpleMarkerSymbol {
    return new SimpleMarkerSymbol({
      style: 'circle',
      color,
      size,
      outline: outlineColor
        ? {
            color: outlineColor,
            width: outlineWidth
          }
        : undefined
    });
  }

  /**
   * Create a picture marker symbol from URL
   * @param type - Marker type for configuration lookup
   * @returns PictureMarkerSymbol or null if type uses CIM
   */
  createPictureMarkerSymbolForType(type: MarkerType): PictureMarkerSymbol | null {
    const config = this.markerConfigs[type];

    if (!config.url) {
      // This type uses CIM symbols
      return null;
    }

    const cacheKey = `picture-marker-type-${type}`;
    const cached = this.getCached<PictureMarkerSymbol>(cacheKey);
    if (cached) {
      return cached;
    }

    const symbol = new PictureMarkerSymbol({
      url: config.url,
      width: config.width,
      height: config.height
    });

    this.setCache(cacheKey, symbol);
    return symbol;
  }

  // ============================================================================
  // Geometry Helpers
  // ============================================================================

  /**
   * Generate circle geometry for CIM symbols
   * @param centerX - X coordinate of circle center
   * @param centerY - Y coordinate of circle center
   * @param radius - Circle radius
   * @param segments - Number of segments to approximate circle (default: 64)
   * @returns CIM geometry object with circle rings
   */
  generateCircleGeometry(
    centerX: number,
    centerY: number,
    radius: number,
    segments: number = 64
  ): CircleGeometry {
    const points: number[][] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * 2 * Math.PI;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      points.push([x, y]);
    }
    return {
      rings: [points]
    };
  }

  /**
   * Generate circle coordinates as flat array
   * @param centerX - X coordinate of center
   * @param centerY - Y coordinate of center
   * @param radius - Radius of circle
   * @param segments - Number of segments (higher = smoother)
   * @returns Array of [x, y] coordinate pairs
   */
  generateCircleCoordinates(
    centerX: number,
    centerY: number,
    radius: number,
    segments: number = 64
  ): number[][] {
    const coordinates: number[][] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      coordinates.push([x, y]);
    }
    return coordinates;
  }

  // ============================================================================
  // CIM Symbol Builders
  // ============================================================================

  /**
   * Create a basic CIM circle symbol
   * @param color - Fill color
   * @param size - Symbol size in pixels
   * @param outlineColor - Optional outline color
   */
  createCIMCircleSymbol(
    color: RGBAColor,
    size: number = 32,
    outlineColor?: RGBAColor
  ): CIMSymbol {
    const halfSize = size / 2;
    const symbolLayers: any[] = [
      {
        type: 'CIMVectorMarker',
        enable: true,
        size,
        frame: {
          xmin: 0,
          ymin: 0,
          xmax: size,
          ymax: size
        },
        markerGraphics: [
          {
            type: 'CIMMarkerGraphic',
            geometry: {
              rings: [this.generateCircleCoordinates(halfSize, halfSize, halfSize, 32)]
            },
            symbol: {
              type: 'CIMPolygonSymbol',
              symbolLayers: [
                {
                  type: 'CIMSolidFill',
                  enable: true,
                  color
                },
                ...(outlineColor
                  ? [
                      {
                        type: 'CIMSolidStroke',
                        enable: true,
                        color: outlineColor,
                        width: 1
                      }
                    ]
                  : [])
              ]
            }
          }
        ]
      }
    ];

    return new CIMSymbol({
      data: {
        type: 'CIMSymbolReference',
        symbol: {
          type: 'CIMPointSymbol',
          symbolLayers
        }
      }
    });
  }

  /**
   * Create cluster symbol matching design specs
   * Blue circle (32px) for clustering multiple items
   * @param color - Optional custom color (defaults to primary blue)
   */
  createClusterSymbol(color: RGBAColor = MapSymbolFactory.COLOR_PRIMARY_BLUE): CIMSymbol {
    const cacheKey = `cluster-${color.join('-')}`;
    const cached = this.getCached<CIMSymbol>(cacheKey);
    if (cached) {
      return cached;
    }

    const symbol = this.createCIMCircleSymbol(color, 32);
    this.setCache(cacheKey, symbol);
    return symbol;
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Ensure base64 image URL has proper prefix
   * @param imageUrl - Image URL (may or may not have prefix)
   * @returns URL with proper data:image/png;base64, prefix
   */
  ensureBase64Prefix(imageUrl: string): string {
    if (imageUrl.startsWith('data:image/png;base64,')) {
      return imageUrl;
    }
    return `data:image/png;base64,${imageUrl}`;
  }

  /**
   * Check if a base64 image is valid (not empty/placeholder)
   * Valid thumbnails should be at least 1000+ characters
   * @param imageUrl - Base64 image URL
   * @returns true if image appears valid
   */
  isValidBase64Image(imageUrl: string): boolean {
    const fullUrl = this.ensureBase64Prefix(imageUrl);
    return fullUrl.length > 1000;
  }

  /**
   * Generate a cache key for picture markers
   * @param uuid - Unique identifier
   * @param rotation - Rotation angle
   * @param showArrow - Whether arrow is shown
   */
  generatePictureCacheKey(uuid: string, rotation: number, showArrow: boolean): string {
    return `picture-${uuid}-${rotation}-${showArrow ? 'arrow' : 'circle'}`;
  }

  /**
   * Generate a cache key for same-location markers
   * @param uuid - Unique identifier
   * @param rotation - Rotation angle
   * @param count - Number of items
   * @param showArrow - Whether arrow is shown
   */
  generateSameLocationCacheKey(
    uuid: string,
    rotation: number,
    count: number,
    showArrow: boolean
  ): string {
    return `same-location-${uuid}-${rotation}-${count}-${showArrow ? 'arrow' : 'circle'}`;
  }

  // ============================================================================
  // Picture Marker Symbols
  // ============================================================================

  /**
   * Create CIMSymbol for picture marker with circular thumbnail.
   *
   * When `showArrow` is true, renders a teardrop pin whose tip points in the
   * compass `rotation` (0=N, clockwise) — the photo sits inside the pin's
   * head and stays upright. When false, renders a plain circular thumbnail.
   *
   * @param circularImageUrl - Base64 PNG URL of circular thumbnail
   * @param rotation - Compass bearing in degrees (0-360)
   * @param uuid - Unique identifier (used for caching)
   * @param showArrow - Render the directional pin shape (default: true)
   */
  createPictureMarkerSymbol(
    circularImageUrl: string,
    rotation: number,
    uuid: string,
    showArrow: boolean = true
  ): CIMSymbol {
    const cacheKey = this.generatePictureCacheKey(uuid, Math.round(rotation || 0), showArrow);
    const cached = this.getCached<CIMSymbol>(cacheKey);
    if (cached) {
      return cached;
    }

    const imageUrl = this.ensureBase64Prefix(circularImageUrl);
    const hasValidImage = this.isValidBase64Image(circularImageUrl);

    if (!hasValidImage) {
      console.warn('⚠️ Skipping cache for invalid/small image:', imageUrl.substring(0, 50));
    }

    const symbol = showArrow
      ? this.buildPinShapePictureSymbol(imageUrl, rotation || 0)
      : this.buildCircularPictureSymbol(imageUrl);

    if (hasValidImage) {
      this.setCache(cacheKey, symbol);
    }
    return symbol;
  }

  /**
   * Circular photo marker (40px) — used when photo has no bearing.
   */
  private buildCircularPictureSymbol(imageUrl: string): CIMSymbol {
    return new CIMSymbol({
      data: {
        type: 'CIMSymbolReference',
        symbol: {
          type: 'CIMPointSymbol',
          symbolLayers: [
            {
              type: 'CIMVectorMarker',
              enable: true,
              anchorPointUnits: 'Relative',
              dominantSizeAxis3D: 'Z',
              size: 40,
              billboardMode3D: 'FaceNearPlane',
              rotation: 0,
              frame: { xmin: 0, ymin: 0, xmax: 40, ymax: 40 },
              markerGraphics: [
                {
                  type: 'CIMMarkerGraphic',
                  geometry: this.generateCircleGeometry(20, 18, 20, 128),
                  symbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [
                      { type: 'CIMSolidFill', enable: true, color: MapSymbolFactory.COLOR_SHADOW },
                    ],
                  } as any,
                },
                {
                  type: 'CIMMarkerGraphic',
                  geometry: this.generateCircleGeometry(20, 20, 20, 128),
                  symbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [
                      { type: 'CIMSolidFill', enable: true, color: MapSymbolFactory.COLOR_WHITE },
                    ],
                  } as any,
                },
                this.buildPhotoMarkerGraphic(20, 20, 18, imageUrl),
              ],
              scaleSymbolsProportionally: true,
              respectFrame: true,
            } as any,
          ],
          haloSize: 1,
          scaleX: 1,
          angleAlignment: 'Display',
          version: '2.0.0',
        } as any,
      },
    });
  }

  /**
   * Pin-shape photo marker (60px bounding, 40px head) — used when photo has
   * a compass bearing. All graphics (pin shadow, pin outline, photo) live in
   * a single CIMVectorMarker layer so the photo reliably draws on top of the
   * pin fill. The tip direction is baked into the pin polygon coordinates
   * rather than rotated, which keeps the embedded photo upright regardless
   * of bearing.
   */
  private buildPinShapePictureSymbol(imageUrl: string, bearingDeg: number): CIMSymbol {
    return new CIMSymbol({
      data: {
        type: 'CIMSymbolReference',
        symbol: {
          type: 'CIMPointSymbol',
          symbolLayers: [
            {
              type: 'CIMVectorMarker',
              enable: true,
              anchorPointUnits: 'Relative',
              dominantSizeAxis3D: 'Z',
              size: 60,
              billboardMode3D: 'FaceNearPlane',
              rotation: 0,
              frame: { xmin: 0, ymin: 0, xmax: 60, ymax: 60 },
              markerGraphics: [
                // Pin shadow (slightly offset down)
                {
                  type: 'CIMMarkerGraphic',
                  geometry: this.generatePinShapeGeometry(30, 28, 20, 26, bearingDeg),
                  symbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [
                      { type: 'CIMSolidFill', enable: true, color: MapSymbolFactory.COLOR_SHADOW },
                    ],
                  } as any,
                },
                // Pin white fill + stroke
                {
                  type: 'CIMMarkerGraphic',
                  geometry: this.generatePinShapeGeometry(30, 30, 20, 26, bearingDeg),
                  symbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [
                      { type: 'CIMSolidFill', enable: true, color: MapSymbolFactory.COLOR_WHITE },
                      { type: 'CIMSolidStroke', enable: true, color: [233, 236, 244, 255], width: 1 },
                    ],
                  } as any,
                },
                // Photo thumbnail (always upright, drawn on top of pin fill)
                this.buildPhotoMarkerGraphic(30, 30, 18, imageUrl),
              ],
              scaleSymbolsProportionally: true,
              respectFrame: true,
            } as any,
          ],
          haloSize: 1,
          scaleX: 1,
          angleAlignment: 'Display',
          version: '2.0.0',
        } as any,
      },
    });
  }

  /**
   * Shared CIMMarkerGraphic that embeds a circular photo thumbnail.
   */
  private buildPhotoMarkerGraphic(cx: number, cy: number, radius: number, imageUrl: string): any {
    return {
      type: 'CIMMarkerGraphic',
      geometry: this.generateCircleGeometry(cx, cy, radius, 128),
      symbol: {
        type: 'CIMPolygonSymbol',
        symbolLayers: [
          {
            type: 'CIMPictureMarker',
            rotation: 0,
            enable: true,
            anchorPointUnits: 'Absolute',
            dominantSizeAxis3D: 'Z',
            offsetX: 0,
            offsetY: 0,
            size: radius * 2,
            billboardMode3D: 'FaceNearPlane',
            invertBackfaceTexture: true,
            scaleX: 1,
            scaleY: 1,
            textureFilter: 'Picture',
            url: imageUrl,
            tintColor: MapSymbolFactory.COLOR_WHITE,
            anchorPoint: { x: 0, y: 0 },
          },
          {
            type: 'CIMSolidFill',
            enable: true,
            color: MapSymbolFactory.COLOR_WHITE,
          },
        ],
        angleAlignment: 'Map',
      },
    };
  }

  /**
   * Teardrop pin polygon with head circle + tangent tip extending in the
   * compass bearing direction (0=N, clockwise). Coordinates are in frame
   * units with +y = north.
   */
  private generatePinShapeGeometry(
    cx: number,
    cy: number,
    headRadius: number,
    tipDist: number,
    bearingDeg: number
  ): CircleGeometry {
    const alphaRad = Math.acos(headRadius / tipDist);
    const alphaDeg = (alphaRad * 180) / Math.PI;
    const toRad = (deg: number) => (deg * Math.PI) / 180;

    const tipX = cx + tipDist * Math.sin(toRad(bearingDeg));
    const tipY = cy + tipDist * Math.cos(toRad(bearingDeg));

    const ring: number[][] = [[tipX, tipY]];
    const startBearing = bearingDeg + alphaDeg;
    const sweep = 360 - 2 * alphaDeg;
    const segments = 48;
    for (let i = 0; i <= segments; i++) {
      const b = startBearing + (sweep * i) / segments;
      const bRad = toRad(b);
      ring.push([
        cx + headRadius * Math.sin(bRad),
        cy + headRadius * Math.cos(bRad),
      ]);
    }
    ring.push([tipX, tipY]);
    return { rings: [ring] };
  }

  /**
   * Create CIMSymbol for same-location pictures with count badge
   * Shows thumbnail in teardrop/circle + small count badge overlay
   * @param circularImageUrl - Base64 PNG URL of circular thumbnail
   * @param rotation - Compass direction in degrees (0-360)
   * @param count - Number of pictures at this location
   * @param uuid - Unique identifier for the same-location group (used for caching)
   * @param showArrow - Whether to show the directional arrow (teardrop) or just circular marker (default: true)
   */
  createSameLocationMarkerSymbol(
    circularImageUrl: string,
    rotation: number,
    count: number,
    uuid: string,
    showArrow: boolean = true
  ): CIMSymbol {
    const cacheKey = this.generateSameLocationCacheKey(uuid, Math.round(rotation || 0), count, showArrow);
    const cached = this.getCached<CIMSymbol>(cacheKey);
    if (cached) {
      return cached;
    }

    const hasValidImage = this.isValidBase64Image(circularImageUrl);

    if (!hasValidImage) {
      console.warn('⚠️ Skipping cache for same-location invalid/small image');
    }

    // Deep-clone the base picture symbol so we never mutate the cached entry.
    const base = this.createPictureMarkerSymbol(circularImageUrl, rotation, uuid, showArrow);
    const clonedData = JSON.parse(JSON.stringify(base.data));
    const pointSymbol = clonedData.symbol;

    // Head center depends on which shape we started with.
    // Circular: frame 40, head at (20, 20). Pin: frame 60, head at (30, 30).
    const frameSize = showArrow ? 60 : 40;
    const headCx = frameSize / 2;
    const headCy = frameSize / 2;
    // Badge sits on the top-right edge of the photo head (+y is up in frame coords).
    // Radius 10 keeps the badge ~55% of the 36px photo — matches Figma proportions.
    const badgeRadius = 10;
    const badgeCx = headCx + 10;
    const badgeCy = headCy + 18;

    // Append the badge shadow + fill to the existing VectorMarker's
    // markerGraphics so they render on top of the photo (later-index
    // graphics draw above earlier ones). A separate symbolLayer draws
    // behind the first layer, which is why we can't use one here.
    const baseVectorMarker = pointSymbol.symbolLayers[0];
    baseVectorMarker.markerGraphics.push({
      type: 'CIMMarkerGraphic',
      geometry: this.generateCircleGeometry(badgeCx + 1, badgeCy - 1, badgeRadius, 128),
      symbol: {
        type: 'CIMPolygonSymbol',
        symbolLayers: [
          { type: 'CIMSolidFill', enable: true, color: MapSymbolFactory.COLOR_SHADOW },
        ],
      },
    });
    baseVectorMarker.markerGraphics.push({
      type: 'CIMMarkerGraphic',
      geometry: this.generateCircleGeometry(badgeCx, badgeCy, badgeRadius, 128),
      symbol: {
        type: 'CIMPolygonSymbol',
        symbolLayers: [
          { type: 'CIMSolidFill', enable: true, color: MapSymbolFactory.COLOR_PRIMARY_BLUE },
        ],
      },
    });

    // Badge text lives in its own VectorMarker layer (CIMTextSymbol inside
    // a markerGraphic doesn't render reliably — needs its own layer with
    // an offset). Pushing it last places it on top, matching the original
    // working pattern.
    const badgeTextFrame = badgeRadius * 2;
    pointSymbol.symbolLayers.push({
      type: 'CIMVectorMarker',
      enable: true,
      anchorPointUnits: 'Relative',
      dominantSizeAxis3D: 'Z',
      size: badgeTextFrame,
      rotation: 0,
      offsetX: badgeCx - headCx,
      offsetY: badgeCy - headCy,
      frame: { xmin: 0, ymin: 0, xmax: badgeTextFrame, ymax: badgeTextFrame },
      markerGraphics: [
        {
          type: 'CIMMarkerGraphic',
          primitiveName: 'badgeTextGraphic',
          // Nudge y up by ~2.5 so the glyph sits visually centered — CIMTextSymbol's
          // `Middle` vertical alignment anchors at the baseline, not the optical centre.
          geometry: { x: badgeRadius, y: badgeRadius + 2.5 },
          symbol: {
            type: 'CIMTextSymbol',
            fontFamilyName: 'Montserrat',
            fontStyleName: 'SemiBold',
            height: 10,
            horizontalAlignment: 'Center',
            verticalAlignment: 'Middle',
            symbol: {
              type: 'CIMPolygonSymbol',
              symbolLayers: [
                { type: 'CIMSolidFill', enable: true, color: MapSymbolFactory.COLOR_WHITE },
              ],
            },
            textString: count > 9 ? '9+' : count.toString(),
          },
        },
      ],
    } as any);

    clonedData.primitiveOverrides = [
      {
        type: 'CIMPrimitiveOverride',
        primitiveName: 'badgeTextGraphic',
        propertyName: 'TextString',
        valueExpressionInfo: {
          type: 'CIMExpressionInfo',
          title: 'Badge Count',
          expression: 'IIF($feature.count > 9, "9+", $feature.count)',
          returnType: 'Default',
        },
      },
    ];

    const symbol = new CIMSymbol({ data: clonedData });

    if (hasValidImage) {
      this.setCache(cacheKey, symbol);
    }
    return symbol;
  }

  /**
   * Create same-location marker symbol with static count (for GraphicsLayer)
   * Uses a simple multi-layer symbol approach since CIM symbols don't work well in GraphicsLayer
   * @param circularImageUrl - Base64 PNG URL of circular thumbnail
   * @returns Simple picture marker config
   */
  createSameLocationMarkerSymbolStatic(circularImageUrl: string): any {
    const imageUrl = this.ensureBase64Prefix(circularImageUrl);

    // Use simple picture marker - GraphicsLayer doesn't support complex CIM well
    // The count will be visible when clicking (carousel shows "3 of 3")
    return {
      type: 'picture-marker',
      url: imageUrl,
      width: '40px',
      height: '40px',
      xoffset: 0,
      yoffset: 10 // Offset up slightly like a teardrop
    };
  }

  /**
   * Create placeholder CIMSymbol with teardrop shape
   * Used as fallback when thumbnail processing fails
   * Matches Figma sizing: 40px height
   */
  createPlaceholderMarkerSymbol(): CIMSymbol {
    const cacheKey = 'placeholder-marker';
    const cached = this.getCached<CIMSymbol>(cacheKey);
    if (cached) {
      return cached;
    }

    // Create a simple white teardrop shape
    const symbol = new CIMSymbol({
      data: {
        type: 'CIMSymbolReference',
        symbol: {
          type: 'CIMPointSymbol',
          symbolLayers: [
            {
              type: 'CIMVectorMarker',
              enable: true,
              anchorPointUnits: 'Relative',
              size: 40,
              frame: {
                xmin: 0,
                ymin: 0,
                xmax: 64,
                ymax: 75,
              },
              markerGraphics: [
                {
                  type: 'CIMMarkerGraphic',
                  geometry: {
                    rings: [
                      [
                        [32, 8.59],
                        [34.02, 10.28],
                        [37.00, 13.02],
                        [38.80, 14.76],
                        [42.39, 18.25],
                        [46.36, 22.34],
                        [48.28, 24.75],
                        [49.50, 26.50],
                        [50.53, 28.23],
                        [51.49, 30.24],
                        [52.18, 32.61],
                        [52.68, 37.37],
                        [52.68, 40.00],
                        [52.40, 42.50],
                        [51.85, 44.90],
                        [51.03, 47.20],
                        [49.50, 50.00],
                        [47.50, 52.50],
                        [45.00, 54.70],
                        [42.00, 56.50],
                        [38.50, 57.60],
                        [32, 58.32],
                        [25.50, 57.60],
                        [22.00, 56.50],
                        [19.00, 54.70],
                        [16.50, 52.50],
                        [14.50, 50.00],
                        [12.97, 47.20],
                        [12.15, 44.90],
                        [11.60, 42.50],
                        [11.32, 40.00],
                        [11.32, 37.37],
                        [11.82, 32.61],
                        [12.51, 30.24],
                        [13.47, 28.23],
                        [14.50, 26.50],
                        [15.72, 24.75],
                        [17.64, 22.34],
                        [21.61, 18.25],
                        [25.20, 14.76],
                        [27.00, 13.02],
                        [29.98, 10.28],
                        [32, 8.59],
                      ],
                    ],
                  },
                  symbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [
                      {
                        type: 'CIMSolidFill',
                        enable: true,
                        color: [245, 245, 245, 255], // Light gray
                      },
                      {
                        type: 'CIMSolidStroke',
                        enable: true,
                        color: [233, 236, 244, 255], // #E9ECF4
                        width: 1,
                      },
                    ],
                  },
                },
              ],
            },
          ],
        },
      },
    });

    this.setCache(cacheKey, symbol);
    return symbol;
  }

  /**
   * Create cluster marker symbol matching Figma design
   * Simple colored circle (32px) with white count text
   * Design: https://www.figma.com/design/Krb8cGxs5bX70BJnoFbbmU?node-id=32841-76958
   * @param siteColor - Optional site color (RGBA array), defaults to blue #1036D6
   */
  createClusterMarkerSymbol(siteColor?: RGBAColor): CIMSymbol {
    const color = siteColor || MapSymbolFactory.COLOR_PRIMARY_BLUE;
    const cacheKey = `cluster-marker-${color.join('-')}`;
    const cached = this.getCached<CIMSymbol>(cacheKey);
    if (cached) {
      return cached;
    }

    const symbol = new CIMSymbol({
      data: {
        type: 'CIMSymbolReference',
        symbol: {
          type: 'CIMPointSymbol',
          symbolLayers: [
            {
              type: 'CIMVectorMarker',
              enable: true,
              size: 32,
              frame: {
                xmin: 0,
                ymin: 0,
                xmax: 32,
                ymax: 32,
              },
              markerGraphics: [
                {
                  type: 'CIMMarkerGraphic',
                  geometry: {
                    rings: [
                      this.generateCircleCoordinates(16, 16, 16, 32)
                    ],
                  },
                  symbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [
                      {
                        type: 'CIMSolidFill',
                        enable: true,
                        color: color,
                      },
                    ],
                  } as any,
                },
              ],
            },
          ],
        },
      },
    });

    this.setCache(cacheKey, symbol);
    return symbol;
  }
}
